/**
 * Funzioni e hook per la gestione delle motivazioni attività
 * @module Pages/Admin/Motivazioni/Functions
 */

import { useEffect, useState } from 'react'
import { FC, invalidateQuery, useCQuery } from '../../../Services'

/**
 * Hook per gestire lo stato di una motivazione
 * @param {string} motivazioneId - ID della motivazione da caricare
 * @returns {Array} [motivazione, setMotivazioneProp, isSuccess]
 */
export const useMotivazione = (motivazioneId) => {
  const [motivazione, setMotivazione] = useState()
  const { data, isSuccess } = useCQuery(['motivazioniAttivita', motivazioneId])

  useEffect(() => {
    if (isSuccess && data) {
      setMotivazione(data)
    }
  }, [isSuccess, data])

  const setMotivazioneProp = (prop, value) =>
    setMotivazione({ ...motivazione, [prop]: value })

  return [motivazione, setMotivazioneProp, isSuccess || motivazioneId === 'new']
}

/**
 * Salva una motivazione (crea nuova o aggiorna esistente)
 * @param {Object} motivazione - Oggetto motivazione da salvare
 * @returns {Promise<Object|boolean>} Risultato del salvataggio o false in caso di errore
 */
export const saveMotivazione = async (motivazione) => {
  if (!motivazione?.codice || !motivazione?.label) {
    window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Inserire codice e label' })
    return false
  }
  if (motivazione.id === 'new') {
    delete motivazione.id
    try {
      const res = await FC.service('motivazioniAttivita').create({ ...motivazione })
      invalidateQuery(['motivazioniAttivita'])
      window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Motivazione creata' })
      return res
    } catch (e) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il salvataggio' })
      return false
    }
  }
  try {
    const res = await FC.service('motivazioniAttivita').patch(motivazione.id, motivazione)
    invalidateQuery(['motivazioniAttivita'])
    window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Motivazione aggiornata' })
    return res
  } catch (e) {
    window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Errore durante il salvataggio' })
    return false
  }
}

/**
 * Elimina (soft delete) una motivazione
 * @param {number} motivazioneId - ID della motivazione da eliminare
 * @returns {Promise<Object>} Risultato dell'eliminazione
 */
export const deleteMotivazione = async (motivazioneId) => {
  const res = await FC.service('motivazioniAttivita').patch(motivazioneId, { deleted: true })
  invalidateQuery(['motivazioniAttivita'])
  window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Motivazione disabilitata' })
  return res
}

/**
 * Riabilita una motivazione precedentemente eliminata
 * @param {number} motivazioneId - ID della motivazione da riabilitare
 * @returns {Promise<Object>} Risultato della riabilitazione
 */
export const restoreMotivazione = async (motivazioneId) => {
  const res = await FC.service('motivazioniAttivita').patch(motivazioneId, { deleted: false })
  invalidateQuery(['motivazioniAttivita'])
  window.growl.show({ severity: 'success', summary: 'Successo', detail: 'Motivazione riabilitata' })
  return res
}
